import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Form, message, Row, Tooltip } from 'antd';
import {
  AddItemButton,
  CustomButton,
  CustomDynamicSelect,
  CustomInput,
  CustomTitle,
} from '../../../components';
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext';
import { AddUsers, GetUserById } from '../../../apis/onboarding';
import { getTeamsDDLOFFeature } from '../../../common/utils/dropDown';
import PhoneInput from 'react-phone-input-2';
import { useSelector } from 'react-redux';
import { GetRoles } from '../../../apis/roles';
import TeamSelectField from '../../../components/TeamSelectField';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import useUnauthorize from '../../../hooks/useUnauthorize';
import theme from '@/assets/theme';
import { DeleteIcon, MailIcon, MailOffIcon } from '@/assets/icons/icons';
import './SettingsInviteUserForm.scss';

const SettingsInviteUserForm = ({ form, id, handleCloseDrawer }) => {
  const { token, getUserID } = useToken();
  const { unauthorize } = useUnauthorize();
  const [teamRoleHistory, setTeamRoleHistory] = useState({});
  const [sendEmail, setSendEmail] = useState(true);
  const isSameUser = Number(id) === Number(getUserID());
  const { showSpin, hideSpin } = useSpin();
  const [isEdit] = useState(id ? true : false);
  const [isFormValid, setIsFormValid] = useState(false);
  const rolePriority = {
    Admin: 3,
    Member: 2,
    Viewer: 1,
  };

  //teams for drop down
  const teamsList = useSelector((state) => state.teams.list);
  const [teamsSearch, setTeamsSearch] = useState('');

  const hasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );

  const [allTeams, setAllTeams] = useState([]);

  const fetchTeams = useCallback(() => {
    getTeamsDDLOFFeature(token, 'Users', teamsSearch).then((val) => {
      setAllTeams(val);
    });
  }, [token, teamsSearch]);

  const handleTeamsSearch = useCallback((val) => {
    setTeamsSearch(val);
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const [roleList, setRoleList] = useState([]);

  const fetchUserById = useCallback(
    (id) => {
      showSpin();
      GetUserById(token, id)
        .then((res) => {
          const data = res?.data ? res.data : null;
          if (data) {
            if (data?.data?.is_success) {
              let resp = data?.data?.data ? data?.data?.data : null;
              if (resp) {
                const formattedTeams = Array.isArray(resp.teams)
                  ? resp.teams.map((item) => {
                      const teamObj = item;
                      return {
                        team: teamObj,
                        role: item.role,
                      };
                    })
                  : [];
                const mappedRoles = {};
                formattedTeams.forEach((item) => {
                  const teamId = item?.team?.id;
                  const roleId = item?.role;
                  const roleName = item?.team?.role_name;

                  if (teamId && roleId && roleName) {
                    mappedRoles[teamId] = { roleId, roleName };
                  }
                });
                setTeamRoleHistory(mappedRoles);

                form.setFieldsValue({
                  name: resp.name || '',
                  id: resp.id || null,
                  email: resp.email || '',
                  role: resp.role || '',
                  title: resp.title || '',
                  phone_number: resp.phone_number || '',
                  teams: formattedTeams,
                });
              }
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    },
    [form, hideSpin, showSpin, token, unauthorize]
  );

  //get user by Id
  useEffect(() => {
    if (id) {
      fetchUserById(id);
    }
  }, [fetchUserById, id]);

  const onFinish = (values) => {
    if (values && Object.keys(values).length > 0) {
      const formattedTeams = values.teams?.map((item) => ({
        team: Array.isArray(item.team)
          ? item.team[0]?.id
          : item.team?.id || item.team,
        role: item.role,
      }));

      const model = {
        ...values,
        teams: formattedTeams,
        id: id && id !== '' ? id : null,
        send_email: sendEmail,
      };

      handleSendInviteUser(model);
    }
  };

  const handleSendInviteUser = (model) => {
    setIsFormValid(true);
    showSpin();
    AddUsers(token, model, false)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            form.resetFields();
            handleCloseDrawer();
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  // default values are set on drawer open
  useEffect(() => {
    form.setFieldsValue({
      teams: [{ team: undefined, role: undefined }],
    });
  }, [form]);

  const handleDisableRole = (data) => {
    const updatedRoleList = roleList.map((item) => {
      // Disable "admin" role only if data has length 1 and includes a "default" item
      const shouldDisableAdmin =
        data?.length === 1 &&
        data.some((x) => x.name.toLowerCase() === 'default');

      // Set "admin" role to disabled if the condition is met, otherwise enable it
      return {
        ...item,
        disabled:
          item.name.toLowerCase() === 'admin' ? shouldDisableAdmin : false,
      };
    });

    setRoleList(updatedRoleList);
  };

  const fetchAllRoles = useCallback(() => {
    showSpin();
    GetRoles(token)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let arr = data?.data?.data.length > 0 ? data?.data?.data : [];
            if (arr.length > 0) {
              setRoleList(arr);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [hideSpin, showSpin, token, unauthorize]);

  useEffect(() => {
    fetchAllRoles();
  }, [fetchAllRoles]);

  return (
    <>
      <Form
        form={form}
        name='userSettingsForm'
        layout='vertical'
        onFinish={onFinish}
        autoComplete='off'
        className='drawer-children-h flex flex-col justify-between'
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {/* {theme.themeType === 4 && (  ZD-915 */}
        <div className='email-mail-box'>
          <Tooltip
            classNames={{ root: 'font-10' }}
            title={`Send 'Set password' email: ${sendEmail ? 'ON' : 'OFF'}`}
          >
            <span>
              {sendEmail ? (
                <div onClick={() => setSendEmail(false)}>
                  <MailIcon
                    width='16px'
                    height='16px'
                    color='var(--text-primary)'
                    className='c-pointer'
                  />
                </div>
              ) : (
                <div onClick={() => setSendEmail(true)}>
                  <MailOffIcon
                    width='16px'
                    height='16px'
                    color='var(--danger)'
                    className='c-pointer'
                  />
                </div>
              )}
            </span>
          </Tooltip>
        </div>
        {/* )} */}
        <div className='drawer-title-header'>
          <CustomTitle
            text={id ? 'Edit user' : 'Invite user'}
            fontSize={20}
            fontWeight='700'
            lineHeight='32px'
            color='var(--text-primary)'
            marginBottom={0}
          />
        </div>

        <div className='invite-user-form-body custom-scrollbar'>
          <Row gutter={[16, 16]}>
            {/* name  */}
            <Col span={24}>
              <Form.Item
                className='custom-input form-input-style'
                label='Name'
                name='name'
                rules={[
                  { required: true, message: 'Please enter name' },
                  {
                    type: 'string',
                    min: 3,
                    message: 'Name must be at least 3 characters long',
                  },
                ]}
                style={{ marginBottom: '0px' }}
              >
                <CustomInput
                  placeholder='Enter name'
                  height='36px'
                  width={'100%'}
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            {/* title */}
            <Col span={24}>
              <Form.Item
                className='custom-input form-input-style'
                label='Title'
                name='title'
                rules={[
                  { required: true, message: 'Please enter title' },
                  {
                    type: 'string',
                    min: 2,
                    message: 'Title must be at least 2 characters long',
                  },
                ]}
                style={{ marginBottom: '0px' }}
              >
                <CustomInput
                  placeholder='Enter title'
                  height='36px'
                  width={'100%'}
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            {/* email  */}
            <Col span={24}>
              <Form.Item
                className='custom-input form-input-style'
                label='Email'
                name='email'
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Enter valid email' },
                ]}
                style={{ marginBottom: '0px' }}
              >
                <CustomInput
                  placeholder='Enter email'
                  height='36px'
                  width={'100%'}
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            {/* phone_number */}
            <Col span={24}>
              <Form.Item
                className='custom-input form-input-style'
                label={'Phone number (optional)'}
                name='phone_number'
                style={{ marginBottom: '0px' }}
                initialValue=''
                // rules={[
                //   {
                //     validator: (_, value) => {
                //       if (!value || value.trim() === "") {
                //         return Promise.resolve();
                //       }
                //       const digitsOnly = value.replace(/\D/g, "");

                //       if (digitsOnly.length >= 10) {
                //         return Promise.resolve();
                //       }

                //       return Promise.reject(new Error("Please enter a valid phone number"));
                //     },
                //   },
                // ]}

                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve(); // Optional
                      }
                      try {
                        // Align with backend parse + InviteUserModal: possible-number
                        // check with US default so normalized "+1 (773) 726-2053"
                        // and legacy bare digits don't error on edit.
                        if (isPossiblePhoneNumber(value, 'US')) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error('Invalid phone number')
                          );
                        }
                      } catch {
                        return Promise.reject(
                          new Error('Invalid phone number')
                        );
                      }
                    },
                  },
                ]}
              >
                <PhoneInput
                  country={'us'}
                  value={form.getFieldValue('phone_number')}
                  onChange={(value, countryData, e, formattedValue) => {
                    form.setFieldsValue({ phone_number: formattedValue });
                  }}
                />
              </Form.Item>
            </Col>

            {/* teams + role  */}

            <>
              <Form.List name='teams'>
                {(fields, { add, remove }) => (
                  <>
                    <Col span={24} style={{ padding: '0' }} vertical={true}>
                      <Col span={24}>
                        <div className='invite-user-form-team-label'>
                          <>
                            {hasValidTeams
                              ? `${theme.themeType === 2 ? 'Client + role' : 'Team + role'}`
                              : 'Role'}{' '}
                            <span className='invite-user-form-required'>*</span>
                          </>
                        </div>
                      </Col>

                      <Col span={24}>
                        <Row gutter={[8, 8]}>
                          {fields.map(({ key, name, ...restField }, index) => {
                            const selectedTeamIds = form
                              .getFieldValue('teams')
                              ?.map(
                                (field, i) => i !== index && field?.team?.id
                              )
                              ?.filter(Boolean);

                            const teamValue = form.getFieldValue([
                              'teams',
                              name,
                              'team',
                            ]);

                            const isAlreadySelected = selectedTeamIds?.includes(
                              teamValue?.id
                            );

                            const isDisabled = isAlreadySelected;

                            return (
                              <React.Fragment key={key}>
                                {hasValidTeams && (
                                  <Col
                                    span={11}
                                    style={{
                                      pointerEvents: isDisabled
                                        ? 'none'
                                        : 'auto',
                                      cursor: isDisabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                      opacity: isDisabled ? 1 : 1,
                                    }}
                                  >
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'team']}
                                      rules={[
                                        {
                                          required: true,
                                          message: `Select a ${theme.themeType === 2 ? 'client' : 'team'}`,
                                        },
                                      ]}
                                      style={{
                                        width: '100%',
                                        fontSize: '12px',
                                        borderRadius: '8px',
                                        pointerEvents: isDisabled
                                          ? 'none'
                                          : 'auto',
                                      }}
                                    >
                                      <TeamSelectField
                                        name={name}
                                        allTeams={allTeams}
                                        isEdit={isEdit}
                                        onTeamChange={handleDisableRole}
                                        selectedTeamIds={selectedTeamIds}
                                        onSearch={handleTeamsSearch}
                                        teamsSearch={teamsSearch}
                                      />
                                    </Form.Item>
                                  </Col>
                                )}
                                <Col
                                  span={hasValidTeams ? 11 : 22}
                                  style={{
                                    pointerEvents: isDisabled ? 'none' : 'auto',
                                  }}
                                >
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'role']}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Select a role',
                                      },
                                    ]}
                                    style={{
                                      width: '100%',
                                      fontSize: '12px',
                                      borderRadius: '8px',
                                      pointerEvents: isDisabled
                                        ? 'none'
                                        : 'auto',
                                    }}
                                  >
                                    <CustomDynamicSelect
                                      className='form-input-style'
                                      placeholder='Select'
                                      disabled={false}
                                      width={'100%'}
                                      height='36px'
                                      border='0px'
                                      bgColor='var(--light-gray)'
                                      textColor='var(--text-secondary)'
                                      borderRadius='8px'
                                      options={roleList.map((role) => {
                                        const selectedTeam = form.getFieldValue(
                                          ['teams', name, 'team']
                                        );
                                        const previouslyAssigned =
                                          teamRoleHistory?.[selectedTeam?.id];

                                        const shouldDisable =
                                          isEdit &&
                                          isSameUser &&
                                          previouslyAssigned &&
                                          rolePriority[role.name] >
                                            rolePriority[
                                              previouslyAssigned.roleName
                                            ];

                                        return {
                                          ...role,
                                          label: role.name,
                                          value: role.id,
                                          disabled: shouldDisable,
                                        };
                                      })}
                                    />
                                  </Form.Item>
                                </Col>

                                <Col
                                  span={2}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  {fields.length > 0 && (
                                    <button
                                      type='button'
                                      aria-label='Remove'
                                      disabled={!hasValidTeams}
                                      className={`invite-user-form-delete-icon${hasValidTeams ? ' is-enabled' : ''}`}
                                      onClick={() => remove(index)}
                                    >
                                      <DeleteIcon color='var(--text-primary)' />
                                    </button>
                                  )}
                                </Col>
                              </React.Fragment>
                            );
                          })}
                        </Row>
                      </Col>
                    </Col>

                    {hasValidTeams && (
                      <Col span={24}>
                        <AddItemButton
                          marginTop='0px'
                          fontSize='12px'
                          fontWeight='600'
                          text={` ${id ? 'Assign' : 'Add'} ${hasValidTeams ? 'team + role' : 'role'}  `}
                          onClick={() => {
                            add();
                          }}
                        />
                      </Col>
                    )}
                  </>
                )}
              </Form.List>
              <Col span={24}>
                <p className='invite-user-text'>
                  Teams control what content and data a user sees. Roles control
                  what a user can do. Learn more about roles{' '}
                  <span>
                    {' '}
                    <Link to='/settings/roles'> here</Link>
                  </span>
                  .
                </p>
              </Col>
            </>
          </Row>

          {/* form bottom buttons  */}
          <div className='drawer-custom-footer'>
            <Row gutter={[0, 8]} style={{ marginTop: '0px' }}>
              <CustomButton
                text={id ? 'Save' : 'Invite'}
                width={'100%'}
                height={48}
                borderRadius={8}
                bgColor={isFormValid ? '#F3F3F3' : '#000'}
                color={isFormValid ? '#A6A6A6' : '#fff'}
                border='0px'
                fontSize={16}
                fontWeight={600}
                htmlType='submit'
                isFormValid={isFormValid}
              />
              <CustomButton
                key='button1'
                width={'100%'}
                onClick={() => handleCloseDrawer()}
                text={'Cancel'}
                bgColor='transparent'
                color='var(--text-primary)'
                fontSize='16px'
                fontWeight='600'
                boxShadow='none'
                height='48px'
              />
            </Row>
          </div>
        </div>
      </Form>
    </>
  );
};

export default SettingsInviteUserForm;
